import { useCallback, useEffect, useState } from 'react';
import { authApi, type AuthCredentials } from '../api/auth';
import { ApiError } from '../api/client';
import type { RegistrationMode, User } from '../types/api';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export function useAuth() {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>('closed');
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  useEffect(() => {
    const callbackError = googleCallbackError();
    authApi.me()
      .then(payload => {
        setUser(payload.user);
        setRegistrationMode(payload.registrationMode ?? 'invite');
        setGoogleAuthEnabled(Boolean(payload.googleAuthEnabled));
        setStatus('authenticated');
      })
      .catch(error => {
        if (error instanceof ApiError && error.status === 401) {
          const payload = error.payload as { registrationMode?: RegistrationMode; googleAuthEnabled?: boolean };
          setRegistrationMode(payload.registrationMode ?? 'invite');
          setGoogleAuthEnabled(Boolean(payload.googleAuthEnabled));
          setServiceError(callbackError);
        } else {
          setServiceError('Tjenesten er ikke tilgjengelig akkurat nå.');
        }
        setStatus('unauthenticated');
      });
  }, []);

  const authenticate = useCallback(async (mode: 'login' | 'register', credentials: AuthCredentials) => {
    const payload = mode === 'login'
      ? await authApi.login(credentials)
      : await authApi.register(credentials);
    setUser(payload.user);
    setStatus('authenticated');
    setServiceError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  return { status, user, registrationMode, googleAuthEnabled, serviceError, authenticate, logout };
}

function googleCallbackError() {
  const parameters = new URLSearchParams(window.location.search);
  const code = parameters.get('authError');
  if (!code) return null;
  parameters.delete('authError');
  const query = parameters.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  if (code === 'google_cancelled') return 'Google-innloggingen ble avbrutt.';
  if (code === 'registration_closed') return 'Google-kontoen er ikke knyttet til en Mellomrom-konto, og registrering er ikke åpen ennå.';
  return 'Kunne ikke logge inn med Google. Prøv igjen.';
}
